import { SessionNotificationsArray } from '../../../interfaces/config'
import { Nullable } from '../../commons/types'

export interface ISessionVariables {
  storefrontName?: string
  sessionUid?: string
}

export enum RecommendedProductsValues {
  ML = 'ML',
  NONE = 'NONE',
}

export enum PrefillCartValues {
  LAST_ORDER = 'LAST_ORDER',
  ML = 'ML',
  NONE = 'NONE',
}

export enum WorkflowChannel {
  WHATSAPP = 'whatsapp',
  LINE = 'line',
}

interface WorkflowDictionary
  extends Record<
    string,
    {
      name?: string
      channel?: `${WorkflowChannel}`
      channelUid?: string
      stephook?: any
      jwtToken?: string
      body?: any
      ng: boolean
    }
  > {}

export interface CheckoutRulesFields {
  quantity: number
  message: string
  isActive: boolean
}

interface ProductCheckoutRules extends CheckoutRulesFields {
  sku: string[]
}

interface StockConfig {
  id: string
  warningMessage: string
  quantity: number
  enabled: boolean
}

export enum CheckoutRulesKeys {
  cartConditionPendingMinAmount = 'cartConditionPendingMinAmount',
  cartConditionPendingMaxAmount = 'cartConditionPendingMaxAmount',
  cartConditionPendingMinQty = 'cartConditionPendingMinQty',
  cartConditionPendingMaxQty = 'cartConditionPendingMaxQty',
  cartConditionPendingUserValidation = 'cartConditionPendingUserValidation',
  cartWarningCheckReturnables = 'cartWarningCheckReturnables',
}

export interface ICheckoutRules {
  [CheckoutRulesKeys.cartConditionPendingMinAmount]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMaxAmount]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMinQty]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMaxQty]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingUserValidation]: ProductCheckoutRules
  [CheckoutRulesKeys.cartWarningCheckReturnables]: ProductCheckoutRules
}

/**
 * Enum representing the types of quantity adjustment that can be applied during cart or order processing.
 *
 * - NONE: No adjustment is performed; quantities are used as provided.
 * - VALIDATE: Quantity is validated against certain criteria (such as stock or business rules), but not changed.
 * - ROUND: Quantity may be rounded according to pallets, layers, jumpQty, or other constraints.
 */
export enum QuantityAdjustment {
  /**
   * No adjustments applied to the quantity.
   */
  NONE = 'NONE',

  /**
   * Validate the quantity, but do not change it.
   */
  VALIDATE = 'VALIDATE',

  /**
   * Round the quantity as per applicable rules or constraints.
   */
  ROUND = 'ROUND',
}

interface ISessionCart {
  quantityAdjustment: `${QuantityAdjustment}`
}

interface SessionConfiguration {
  stock?: {
    hideOutOfStock?: boolean
    enabled?: boolean
    stockThreshold?: number
    key?: string
    threshold: {
      criticalLowWarning: StockConfig
      lowWarning: StockConfig
    }
  }
  i18n?: {
    language?: string
    currencyFormat?: string
    currencySymbol?: string
    currencyCode?: string
    country?: string
  }
  workflow?: WorkflowDictionary
  checkoutRules?: ICheckoutRules
  groupers?: {
    cart: {
      active: boolean
      session: string[]
      product: string[]
    }
    order: {
      active: boolean
      session: string[]
      product: string[]
    }
  }
  showRecommendations?: boolean
  filters?: {
    attributes: string[]
    promotions: {
      active?: boolean
    }
    categories: {
      active?: boolean
    }
    brands: {
      active?: boolean
    }
  }
  promotions?: {
    autoAdd: boolean
    disableFreeGoodsAsDiscount: boolean | null
  }
  recommendedProducts?: {
    prefillCart: PrefillCartValues
    frequentlyBoughtTogether: RecommendedProductsValues
    suggestedItems: RecommendedProductsValues
  }
  notifications?: SessionNotificationsArray
  cart: Nullable<ISessionCart>
}

export interface Banner {
  id: string
  imagesURL: string[]
  customerType: string
  reference: string
  type: string
  isActive: boolean
  priority: number
  startDate: Date
  expirationDate: Date
  userAgent: string | null
}

export interface Customer {
  email?: string
  phoneNumber?: string[]
  code?: string
  type?: string
}

export type extraId = {
  key: string
  value: string
}

export interface SessionDataArgs {
  id?: string
  customerUid?: string
  workflow?: {
    name?: string
    channel?: `${WorkflowChannel}`
    channelUid?: string
    userUid?: string
    workflowName?: string
  }
  configuration?: SessionConfiguration
  banners?: Banner[]
  customer?: Customer
  extraIds?: extraId[]
  ownerInfo?: {
    slug: string | null
  }
}

export interface ISession {
  session: SessionDataArgs
}
